/** Real client for `/api/v1/comments/*` (api/app/comments_api.py). Same origin + session cookie as the chat API. */
import { ApiError } from '../client'
import { reportUnauthorized } from '../http'
import type {
  CommentCountsResponse,
  CommentDetail,
  CommentListQuery,
  CommentPage,
  CommentReplyBody,
  CommentsApi,
  CommentSettings,
  CommentThread,
} from './types'

type Query = Record<string, string | number | null | undefined>

export class HttpCommentsApi implements CommentsApi {
  constructor(
    private readonly base: string,
    private readonly timeoutMs = 15000,
    /** replies wait for Meta (the server's own timeout is 15 s) */
    private readonly sendTimeoutMs = 30000,
    private readonly fetchImpl: typeof fetch = (...a) => fetch(...a),
  ) {}

  private url(path: string, q?: Query): string {
    const u = new URL(`${this.base.replace(/\/+$/, '')}/api/v1/comments${path}`, window.location.origin)
    for (const [k, v] of Object.entries(q ?? {})) {
      if (v !== null && v !== undefined && v !== '') u.searchParams.set(k, String(v))
    }
    return u.toString()
  }

  private async req<T>(method: string, path: string, opts: { query?: Query; body?: unknown; timeoutMs?: number } = {}): Promise<T> {
    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), opts.timeoutMs ?? this.timeoutMs)
    const headers: Record<string, string> = { Accept: 'application/json' }
    if (opts.body !== undefined) headers['Content-Type'] = 'application/json'
    try {
      const res = await this.fetchImpl(this.url(path, opts.query), {
        method,
        headers,
        body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
        credentials: 'same-origin',
        signal: ctrl.signal,
      })
      if (res.status === 401) reportUnauthorized()
      if (!res.ok) {
        let detail = res.statusText
        try {
          const j = (await res.json()) as { detail?: unknown }
          if (typeof j.detail === 'string') detail = j.detail
        } catch {
          /* body not JSON */
        }
        throw new ApiError(res.status, detail || `HTTP ${res.status}`)
      }
      return (await res.json()) as T
    } catch (e) {
      if (e instanceof ApiError) throw e
      if (e instanceof DOMException && e.name === 'AbortError') throw new ApiError(0, 'หมดเวลารอเซิร์ฟเวอร์ ตรวจผลก่อนกดอีกครั้ง')
      throw new ApiError(0, 'ต่อเซิร์ฟเวอร์ไม่ได้')
    } finally {
      clearTimeout(timer)
    }
  }

  list(q: CommentListQuery): Promise<CommentPage> {
    return this.req('GET', '', {
      query: { tab: q.tab, channel: q.channel, post: q.post, q: q.q, cursor: q.cursor, limit: q.limit },
    })
  }

  counts(): Promise<CommentCountsResponse> {
    return this.req('GET', '/counts')
  }

  get(id: string): Promise<CommentDetail> {
    return this.req('GET', `/${encodeURIComponent(id)}`)
  }

  thread(id: string, before: string | null): Promise<CommentThread> {
    return this.req('GET', `/${encodeURIComponent(id)}/thread`, { query: { before } })
  }

  reply(id: string, body: CommentReplyBody): Promise<CommentDetail> {
    return this.req('POST', `/${encodeURIComponent(id)}/reply`, { body, timeoutMs: this.sendTimeoutMs })
  }

  privateReply(id: string, body: CommentReplyBody): Promise<CommentDetail> {
    return this.req('POST', `/${encodeURIComponent(id)}/private-reply`, { body, timeoutMs: this.sendTimeoutMs })
  }

  hide(id: string, hidden: boolean): Promise<CommentDetail> {
    return this.req('POST', `/${encodeURIComponent(id)}/hide`, { body: { hidden }, timeoutMs: this.sendTimeoutMs })
  }

  skip(id: string, skip: boolean): Promise<CommentDetail> {
    return this.req('POST', `/${encodeURIComponent(id)}/skip`, { body: { skip } })
  }

  async ofConversation(conversationId: string): Promise<string | null> {
    const r = await this.req<{ comment_id: string | null }>('GET', `/of-conversation/${encodeURIComponent(conversationId)}`)
    return r.comment_id
  }

  getSettings(): Promise<CommentSettings> {
    return this.req('GET', '/settings')
  }

  putSettings(patch: Partial<Omit<CommentSettings, 'can_edit'>>): Promise<CommentSettings> {
    return this.req('PUT', '/settings', { body: patch })
  }
}
